import React, {useEffect, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {toast} from "react-toastify";
import {RefreshCw, UserCheck, UserX} from "react-feather";
import {ModuleDataTable, ModuleLayout} from "../Common/ModuleLayout";
import ConfirmDeleteDialog from "../Common/ConfirmDeleteDialog";
import { userService } from "../../services/userService";
import { partnerService } from "../../services/partnerService";
import { projectService } from "../../services/projectService";
import UsersFilter from "./UsersFilter";
import UsersPagination from "./UsersPagination";
import UserModal from "./UserModal";
import PartnersFilter from "./PartnersFilter";
import PartnersPagination from "./PartnersPagination";
import PartnerModal from "./PartnerModal";
import {activityLogsData, permissionsData, rolesData,} from "../../Data/adminDummyData";
import {adminColumnDefinitions} from "../../Data/adminColumns";
import {getAdminViews} from "../../Data/adminViews";
import {getCurrentUserPartnerUuid, isCurrentUserAdmin} from "../../util/auth";

/**
 * Admin Module - Demonstrates the dynamic layout system
 * This module manages users, roles, permissions, partners, and activity logs
 */
const AdminModule = () => {
  const [searchParams] = useSearchParams();
  const urlView = searchParams.get('view');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    users: [], // Will be loaded from API
    roles: rolesData,
    permissions: permissionsData,
    partners: [], // Will be loaded from API
    activityLogs: activityLogsData,
  });
  const [currentView, setCurrentView] = useState(null);

  // Pagination state for users
  const [usersPagination, setUsersPagination] = useState({
    currentPage: 0,
    pageSize: 10,
    totalPages: 0,
    totalElements: 0,
  });

  // Filter state for users
  // ADMIN: partnerUuid is null by default (can filter any partner)
  // Non-ADMIN: partnerUuid is set to their own partner (restricted)
  const [usersFilters, setUsersFilters] = useState({
    partnerUuid: isCurrentUserAdmin() ? null : getCurrentUserPartnerUuid(),
    name: "",
    userType: null,
    active: null,
    sort: "-createdDate", // Default sort: newest first
  });

  // Modal state for users
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: "create", // "create", "edit", or "view"
    selectedUser: null,
  });

  // Pagination state for partners
  const [partnersPagination, setPartnersPagination] = useState({
    currentPage: 0,
    pageSize: 10,
    totalPages: 0,
    totalElements: 0,
  });

  // Filter state for partners
  const [partnersFilters, setPartnersFilters] = useState({
    name: "",
    sort: "-createdAt", // Default sort: newest first
  });

  // Modal state for partners
  const [partnerModalState, setPartnerModalState] = useState({
    isOpen: false,
    mode: "create",
    selectedPartner: null,
  });

  // Single confirm dialog state — covers all confirmation flows
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [confirmExecuting, setConfirmExecuting] = useState(false);
  // Keeps last dialog props alive during the modal's closing animation to prevent flickering
  const lastConfirmProps = useRef({});

  // Define views for the module (memoized to prevent unnecessary re-renders)
  const views = useMemo(
    () => getAdminViews(data.users.length, data.partners.length),
    [data.users.length, data.partners.length]
  );

  // Sync currentView from URL — avoids calling setState inside renderContent (during render)
  useEffect(() => {
    setCurrentView(urlView || "users");
  }, [urlView]);

  // Fetch data when current view changes
  useEffect(() => {
    if (currentView === "users") {
      fetchUsers();
    } else if (currentView === "partners") {
      fetchPartners();
    }
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch users from API
  const fetchUsers = async (page = null, size = null, filters = null) => {
    setLoading(true);
    try {
      const currentPage = page !== null ? page : usersPagination.currentPage;
      const currentSize = size !== null ? size : usersPagination.pageSize;
      const currentFilters = filters || usersFilters;

      const params = {
        page: currentPage,
        size: currentSize,
        ...(currentFilters.sort && { sort: currentFilters.sort }),
        ...(currentFilters.partnerUuid && { partnerUuid: currentFilters.partnerUuid }),
        ...(currentFilters.name?.trim() && { name: currentFilters.name.trim() }),
        ...(currentFilters.userType && { userType: currentFilters.userType }),
        ...(currentFilters.active !== null && currentFilters.active !== undefined && { active: currentFilters.active }),
      };

      const response = await userService.list(params);

      const users = response.content || [];
      setData((prev) => ({ ...prev, users }));
      setUsersPagination({
        currentPage: response.number || 0,
        pageSize: response.size || 10,
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0,
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
      setData((prev) => ({ ...prev, users: [] }));
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    console.log("Page changed to:", newPage);
    setUsersPagination((prev) => ({ ...prev, currentPage: newPage }));
    fetchUsers(newPage, null, null);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    console.log("Page size changed to:", newSize);
    setUsersPagination((prev) => ({ ...prev, pageSize: newSize, currentPage: 0 }));
    fetchUsers(0, newSize, null);
  };

  // Apply filters
  const applyUsersFilters = (newFilters) => {
    console.log("Applying filters:", newFilters);
    setUsersFilters(newFilters);
    setUsersPagination((prev) => ({ ...prev, currentPage: 0 })); // Reset to first page
    fetchUsers(0, null, newFilters);
  };

  // Clear filters
  // ADMIN: clears all including partnerUuid
  // Non-ADMIN: keeps partnerUuid filter
  const clearUsersFilters = () => {
    const defaultFilters = {
      partnerUuid: isCurrentUserAdmin() ? null : getCurrentUserPartnerUuid(),
      name: "",
      userType: null,
      active: null,
      sort: "-createdDate",
    };
    setUsersFilters(defaultFilters);
    setUsersPagination((prev) => ({ ...prev, currentPage: 0 }));
    fetchUsers(0, null, defaultFilters);
  };

  // ============ PARTNERS SECTION ============

  // Fetch partners from API
  const fetchPartners = async (page = null, size = null, filters = null) => {
    setLoading(true);
    try {
      const currentPage = page !== null ? page : partnersPagination.currentPage;
      const currentSize = size !== null ? size : partnersPagination.pageSize;
      const currentFilters = filters || partnersFilters;

      const params = {
        page: currentPage,
        size: currentSize,
        ...(currentFilters.sort && { sort: currentFilters.sort }),
        ...(currentFilters.name?.trim() && { name: currentFilters.name.trim() }),
      };

      const response = await partnerService.list(params);

      const partners = response.content || [];
      setData((prev) => ({ ...prev, partners }));
      setPartnersPagination({
        currentPage: response.page || 0,
        pageSize: response.size || 10,
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0,
      });
    } catch (error) {
      console.error("Error fetching partners:", error);
      toast.error("Failed to load partners");
      setData((prev) => ({ ...prev, partners: [] }));
    } finally {
      setLoading(false);
    }
  };

  // Handle partner page change
  const handlePartnerPageChange = (newPage) => {
    console.log("Partner page changed to:", newPage);
    setPartnersPagination((prev) => ({ ...prev, currentPage: newPage }));
    fetchPartners(newPage, null, null);
  };

  // Handle partner page size change
  const handlePartnerPageSizeChange = (newSize) => {
    console.log("Partner page size changed to:", newSize);
    setPartnersPagination((prev) => ({ ...prev, pageSize: newSize, currentPage: 0 }));
    fetchPartners(0, newSize, null);
  };

  // Apply partner filters
  const applyPartnersFilters = (newFilters) => {
    console.log("Applying partner filters:", newFilters);
    setPartnersFilters(newFilters);
    setPartnersPagination((prev) => ({ ...prev, currentPage: 0 })); // Reset to first page
    fetchPartners(0, null, newFilters);
  };

  // Clear partner filters
  const clearPartnersFilters = () => {
    const defaultFilters = {
      name: "",
      sort: "-createdAt",
    };
    setPartnersFilters(defaultFilters);
    setPartnersPagination((prev) => ({ ...prev, currentPage: 0 }));
    fetchPartners(0, null, defaultFilters);
  };

  // ============ END PARTNERS SECTION ============

  // Column definitions (extracted to Data/adminColumns.js)
  const columnDefinitions = adminColumnDefinitions;

  // Modal toggle functions for users
  const openModal = (mode, user = null) => {
    setModalState({
      isOpen: true,
      mode: mode,
      selectedUser: user,
    });
  };

  const closeModal = () => {
    // Only close — keep mode/selectedUser so the modal animates out with correct content
    // instead of flickering to the Create form during the closing animation.
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleModalSuccess = (response) => {
    console.log("User saved successfully:", response);
    // Refresh users list
    if (currentView === "users") {
      fetchUsers();
    }
  };

  // Modal toggle functions for partners
  const openPartnerModal = (mode, partner = null) => {
    setPartnerModalState({
      isOpen: true,
      mode: mode,
      selectedPartner: partner,
    });
  };

  const closePartnerModal = () => {
    // Only close — keep mode/selectedPartner so the modal animates out with correct content
    // instead of flickering to the Create form during the closing animation.
    setPartnerModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const handlePartnerModalSuccess = (response) => {
    console.log("Partner saved successfully:", response);
    // Refresh partners list
    if (currentView === "partners") {
      fetchPartners();
    }
  };

  // Action handlers
  const handleAdd = (viewId) => {
    console.log(`Add new item for view: ${viewId}`);

    if (viewId === "users") {
      openModal("create");
    } else if (viewId === "partners") {
      openPartnerModal("create");
    } else {
      toast.success(`Add functionality for ${viewId} - Ready for backend integration`);
    }
  };

  const handleView = (viewId, row) => {
    console.log(`View item:`, row);

    if (viewId === "users") {
      openModal("view", row);
    } else if (viewId === "partners") {
      openPartnerModal("view", row);
    } else {
      toast.info(`Viewing ${viewId}: ${row.name || row.user || row.id}`);
    }
  };

  const handleEdit = (viewId, row) => {
    console.log(`Edit item:`, row);

    if (viewId === "users") {
      openModal("edit", row);
    } else if (viewId === "partners") {
      openPartnerModal("edit", row);
    } else {
      toast.info(`Edit functionality for ${viewId} - Ready for backend integration`);
    }
  };

  // ============ CONFIRM DIALOG HELPERS ============

  const openConfirm = (dialogProps, execute) => {
    lastConfirmProps.current = dialogProps;
    setPendingConfirm({dialogProps, execute});
  };

  const runConfirm = async () => {
    if (!pendingConfirm?.execute) return;
    setConfirmExecuting(true);
    try {
      await pendingConfirm.execute();
    } finally {
      setConfirmExecuting(false);
      setPendingConfirm(null);
    }
  };

  // ============ GENERIC DELETE HANDLER ============

  const handleDelete = (viewId, row) => {
    openConfirm(
        {
          title: "Confirm Delete",
          itemName: row.name || row.user || String(row.id),
          actionText: "Delete",
          loadingText: "Deleting...",
        },
        async () => {
          setData((prev) => ({
            ...prev,
            [viewId]: prev[viewId].filter((item) => item.id !== row.id),
          }));
          toast.success("Deleted successfully");
        }
    );
  };

  // ============ USER-SPECIFIC ACTION HANDLERS ============

  const handleUserDelete = (user) => {
    closeModal();
    openConfirm(
        {
          title: "Delete User",
          itemName: user.userName,
          warningText:
              "This action is permanent and cannot be undone. All data associated with this user will be removed.",
          actionText: "Delete User",
          loadingText: "Deleting...",
        },
        async () => {
          try {
            await userService.remove(user.id);
            toast.success(`User "${user.userName}" has been deleted.`);
            fetchUsers();
          } catch (error) {
            console.error("Delete user failed:", error);
            toast.error("Failed to delete user");
          }
        }
    );
  };

  const handleUserToggleActive = (user) => {
    const action = user.active ? "deactivate" : "activate";
    closeModal();
    openConfirm(
        {
          headerText: user.active ? "Confirm Deactivation" : "Confirm Activation",
          title: user.active ? "Deactivate User" : "Activate User",
          icon: user.active ? UserX : UserCheck,
          confirmColor: user.active ? "warning" : "success",
          subtitle: user.active
              ? "The user will lose access to the platform"
              : "The user will regain access to the platform",
          bodyText: `Are you sure you want to ${action} user "${user.userName}"?`,
          actionText: user.active ? "Deactivate" : "Activate",
          loadingText: user.active ? "Deactivating..." : "Activating...",
        },
        async () => {
          try {
            user.active
              ? await userService.deactivate(user.id)
              : await userService.activate(user.id);
            toast.success(`User "${user.userName}" has been ${action}d.`);
            fetchUsers();
          } catch (error) {
            console.error("Toggle active failed:", error);
            toast.error(`Failed to ${action} user`);
          }
        }
    );
  };

  const handleUserResetPassword = (user) => {
    closeModal();
    openConfirm(
        {
          headerText: "Confirm Password Reset",
          title: "Reset Password",
          icon: RefreshCw,
          confirmColor: "warning",
          itemName: user.userName,
          bodyText: `Reset password for "${user.userName}"?`,
          warningText:
              "Their account will be deactivated and they will receive a new temporary password by email.",
          warningLabel: "Note",
          actionText: "Reset Password",
          loadingText: "Resetting...",
        },
        async () => {
          try {
            await userService.resetPassword(user.id);
            toast.success(
                `Password reset for "${user.userName}". A temporary password has been sent to their email.`
            );
            fetchUsers();
          } catch (error) {
            console.error("Reset password failed:", error);
            toast.error("Failed to reset password");
          }
        }
    );
  };

  // ============ PARTNER-SPECIFIC ACTION HANDLERS ============

  const handlePartnerDelete = (partner) => {
    closePartnerModal();
    openConfirm(
        {
          title: "Delete Partner",
          itemName: partner.name,
          warningText:
              "This action is permanent and cannot be undone. All data associated with this partner will be removed.",
          actionText: "Delete Partner",
          loadingText: "Deleting...",
        },
        async () => {
          try {
            await partnerService.remove(partner.uuid);
            toast.success(`Partner "${partner.name}" has been deleted.`);
            fetchPartners();
          } catch (error) {
            console.error("Delete partner failed:", error);
            toast.error("Failed to delete partner");
          }
        }
    );
  };

  const handlePartnerToggleActive = (partner) => {
    const action = partner.active ? "deactivate" : "activate";
    closePartnerModal();
    openConfirm(
        {
          headerText: partner.active ? "Confirm Deactivation" : "Confirm Activation",
          title: partner.active ? "Deactivate Partner" : "Activate Partner",
          icon: partner.active ? UserX : UserCheck,
          confirmColor: partner.active ? "warning" : "success",
          subtitle: partner.active
              ? "Partner users will lose access to the platform"
              : "Partner users will regain access to the platform",
          bodyText: `Are you sure you want to ${action} partner "${partner.name}"?`,
          actionText: partner.active ? "Deactivate" : "Activate",
          loadingText: partner.active ? "Deactivating..." : "Activating...",
        },
        async () => {
          try {
            partner.active
              ? await partnerService.deactivate(partner.uuid)
              : await partnerService.activate(partner.uuid);
            toast.success(`Partner "${partner.name}" has been ${action}d.`);
            fetchPartners();
          } catch (error) {
            console.error("Toggle partner active failed:", error);
            toast.error(`Failed to ${action} partner`);
          }
        }
    );
  };

  const handlePartnerSync = async (partner) => {
    try {
      const result = await projectService.syncPartner(partner.uuid);
      if (result?.status === "FAILED") {
        toast.error(`Sync failed for "${partner.name}": ${result.errorMessage || "Unknown error"}`);
      } else {
        const summary = result && result.newRecords != null
          ? `${result.newRecords} new, ${result.updatedRecords} updated`
          : "completed";
        toast.success(`Sync ${summary} for "${partner.name}"`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to sync "${partner.name}"`);
    }
  };

  const handleSyncAllPartners = () => {
    openConfirm(
      {
        headerText: "Sync All Partners",
        title: "Sync All Partners Projects",
        icon: RefreshCw,
        confirmColor: "warning",
        subtitle: "Re-fetches all project and point data from E2 Cloud",
        bodyText: "This triggers a full sync across every partner. It may take several minutes and places load on the E2 Cloud API.",
        warningText: "Only run this when you need to refresh data for all partners at once.",
        warningLabel: "Note",
        actionText: "Sync All",
        loadingText: "Syncing...",
      },
      async () => {
        try {
          const result = await projectService.syncAllPartners();
          if (result?.status === "FAILED") {
            toast.error(`Sync failed: ${result.errorMessage || "Unknown error"}`);
          } else {
            toast.success("Full sync completed for all partners");
          }
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to start sync");
        }
      }
    );
  };

  const handleLicenseCreate = async (partner, licenseData) => {
    try {
      await partnerService.createLicense(partner.uuid, licenseData);
      toast.success("License created successfully");
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create license");
    }
  };

  const handleLicenseRenew = async (partner, licenseUuid, newEndDate) => {
    try {
      await partnerService.renewLicense(partner.uuid, licenseUuid, { endDate: newEndDate });
      toast.success("License renewed successfully");
      fetchPartners();
    } catch (error) {
      console.error("LicenseRenew failed:", error);
      toast.error("Failed to renew license, please try again later");
    }
  };

  const handleLicenseDelete = (partner, licenseUuid) => {
    closePartnerModal();
    openConfirm(
        {
          title: "Delete License",
          bodyText: "Are you sure you want to delete this license? This action cannot be undone.",
          actionText: "Delete License",
          loadingText: "Deleting...",
        },
        async () => {
          try {
            await partnerService.deleteLicense(partner.uuid, licenseUuid);
            toast.success("License deleted successfully");
            fetchPartners();
          } catch (error) {
            console.error("License delete failed:", error);
            toast.error("Failed to delete license");
          }
        }
    );
  };

  const handleBulkDelete = (viewId, selectedRows) => {
    openConfirm(
        {
          title: `Delete ${selectedRows.length} Items`,
          bodyText: `Are you sure you want to delete ${selectedRows.length} selected items?`,
          actionText: "Delete All",
          loadingText: "Deleting...",
        },
        async () => {
          const idsToDelete = selectedRows.map((row) => row.id);
          setData((prev) => ({
            ...prev,
            [viewId]: prev[viewId].filter((item) => !idsToDelete.includes(item.id)),
          }));
          toast.success(`Deleted ${selectedRows.length} items`);
        }
    );
  };

  const handleRefresh = (viewId) => {
    console.log(`Refresh data for view: ${viewId}`);

    if (viewId === "users") {
      // Fetch fresh data from API
      fetchUsers();
    } else if (viewId === "partners") {
      // Fetch fresh data from API
      fetchPartners();
    } else {
      // For other views, simulate refresh
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        toast.success(`Data refreshed - Ready for backend integration`);
      }, 500);
    }
  };

  // Render content based on active view
  const renderContent = (activeView, viewData) => {
    // Special rendering for users view with filters and pagination
    if (activeView === "users") {
      return (
        <>
          {/* Filters */}
          <UsersFilter
            filters={usersFilters}
            onApplyFilters={applyUsersFilters}
            onClearFilters={clearUsersFilters}
          />

          {/* Data Table */}
          <ModuleDataTable
            title={viewData?.name || "Users"}
            data={data.users}
            columns={columnDefinitions.users}
            loading={loading}
            searchable={false} // Disable client-side search (using server filters)
            addButton={true}
            refreshButton={true}
            actions={{
              onAdd: () => handleAdd(activeView),
              onView: (row) => handleView(activeView, row),
              onEdit: (row) => handleEdit(activeView, row),
              onDelete: (row) => handleUserDelete(row),
              onRefresh: () => handleRefresh(activeView),
            }}
          />

          {/* Pagination */}
          <UsersPagination
            pagination={usersPagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      );
    }

    // Special rendering for partners view with filters and pagination
    if (activeView === "partners") {
      return (
        <>
          {/* Filters */}
          <PartnersFilter
            filters={partnersFilters}
            onApplyFilters={applyPartnersFilters}
            onClearFilters={clearPartnersFilters}
          />

          {/* Sync All button — ADMIN only, expensive operation */}
          <div className="d-flex justify-content-end mb-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={handleSyncAllPartners}
              title="Re-fetch all project and point data from E2 Cloud for every partner"
            >
              <RefreshCw size={13} className="me-1" />
              Sync All Partners Projects
            </button>
          </div>

          {/* Data Table */}
          <ModuleDataTable
            title={viewData?.name || "Partners"}
            data={data.partners}
            columns={columnDefinitions.partners}
            loading={loading}
            searchable={false} // Disable client-side search (using server filters)
            addButton={true}
            refreshButton={true}
            actions={{
              onAdd: () => handleAdd(activeView),
              onView: (row) => handleView(activeView, row),
              onEdit: (row) => handleEdit(activeView, row),
              onDelete: (row) => handlePartnerDelete(row),
              onRefresh: () => handleRefresh(activeView),
            }}
          />

          {/* Pagination */}
          <PartnersPagination
            pagination={partnersPagination}
            onPageChange={handlePartnerPageChange}
            onPageSizeChange={handlePartnerPageSizeChange}
          />
        </>
      );
    }

    // Default rendering for other views
    return (
      <ModuleDataTable
        title={viewData?.name || "Data"}
        data={data[activeView] || []}
        columns={columnDefinitions[activeView] || []}
        loading={loading}
        searchable={true}
        addButton={activeView !== "activityLogs"}
        refreshButton={true}
        actions={{
          onAdd: activeView !== "activityLogs" ? () => handleAdd(activeView) : undefined,
          onView: (row) => handleView(activeView, row),
          onEdit: activeView !== "activityLogs" ? (row) => handleEdit(activeView, row) : undefined,
          onDelete: (row) => handleDelete(activeView, row),
          onBulkDelete: (rows) => handleBulkDelete(activeView, rows),
          onRefresh: () => handleRefresh(activeView),
        }}
      />
    );
  };

  return (
    <>
      <ModuleLayout
        moduleTitle="Admin Management"
        views={views}
        defaultView="users"
        renderContent={renderContent}
        breadcrumbItems={{
          parent: "Dashboard",
          title: "Admin Management",
        }}
        hideViewSelector={true}
        initialView={urlView || "users"}
      />

      {/* User Modal */}
      <UserModal
        isOpen={modalState.isOpen}
        toggle={closeModal}
        mode={modalState.mode}
        userData={modalState.selectedUser}
        onSuccess={handleModalSuccess}
        onDelete={handleUserDelete}
        onToggleActive={handleUserToggleActive}
        onResetPassword={handleUserResetPassword}
      />

      {/* Partner Modal */}
      <PartnerModal
        isOpen={partnerModalState.isOpen}
        toggle={closePartnerModal}
        mode={partnerModalState.mode}
        partnerData={partnerModalState.selectedPartner}
        onSuccess={handlePartnerModalSuccess}
        onDelete={handlePartnerDelete}
        onToggleActive={handlePartnerToggleActive}
        onSyncPartner={handlePartnerSync}
        onLicenseCreate={handleLicenseCreate}
        onLicenseRenew={handleLicenseRenew}
        onLicenseDelete={handleLicenseDelete}
      />

      {/* Single confirm dialog — shared by all destructive/sensitive actions.
          lastConfirmProps keeps props stable during the modal's close animation
          so content doesn't flicker back to defaults while fading out. */}
      <ConfirmDeleteDialog
          isOpen={!!pendingConfirm}
          {...(pendingConfirm?.dialogProps ?? lastConfirmProps.current)}
          loading={confirmExecuting}
          onClose={() => !confirmExecuting && setPendingConfirm(null)}
          onConfirm={runConfirm}
      />
    </>
  );
};

export default AdminModule;
